<template>
  <div class="eventWrapper" @click="$emit('click', $event)">
    <PlannerEvent
      v-if="isWeekView && event"
      :key="event.id"
      :event="event"
      :start="start || event.start"
      :end="end || event.end"
      :task="task || undefined"
      :overlapping-index="overlappingIndex"
      :is-selected="isSelected"
      :is-moving="isMoving"
      :is-right-click-allowed="isRightClickAllowed"
      :is-left-pane-shown="isLeftPaneShown"
      :zoom-level="zoomLevel"
      :read-only="readOnly"
      :calendar-a-p-i="calendarAPI"
      @open="(v, e) => $emit('open', v, e)"
    />
    <PlannerEventLight
      v-else-if="event"
      :event="event"
      :start="start || event.start"
      :end="end || event.end"
      :task="task || undefined"
      :is-selected="isSelected"
      :is-right-click-allowed="isRightClickAllowed"
      @open="(v, e) => $emit('open', v, e)"
    />
  </div>
</template>

<script lang="ts">
import type { Calendar } from "@fullcalendar/core";
import type { Dictionary } from "@fullcalendar/core/internal";
import { isEmpty, isEqual } from "lodash-es";
import { computed, defineComponent, PropType, Ref, toRefs, watch } from "vue";

import type {
  EventId,
  FullEvent,
  ReleasableRef,
  TaskId,
} from "@ctrl/controller-rpc";

import * as inject from "@/components/inject";
import PlannerEvent from "@/components/planner/PlannerEvent.vue";
import PlannerEventLight from "@/components/planner/PlannerEventLight.vue";
import { getDuration } from "@/event";
import { bindAsync, releasableRef, useReleaser, withRelease } from "@/ref";
import { isGeneralizedTaskId } from "@/utils/id";
import { pause } from "@/utils/pause";

import { buildFakeEvent } from "./controllers/event";
import { patchSimultaneousEvents } from "./controllers/events";
import { isStaticGhostEvent } from "./controllers/static-ghost-event";
import { isAggregatedAllDayEvent } from "./helpers/aggregated-all-day-event";
import { getMinimumEventEnd } from "./helpers/get-minimum-event-end";

/**
 * The `eventRef` FullCalendar extendedProp set by `useEvents`' buildEventInput
 * — a live event ref owned by the planner grid. extendedProps is an untyped
 * FullCalendar Dictionary, hence the runtime shape check.
 */
const isSharedEventRef = (
  value: unknown
): value is Readonly<Ref<FullEvent | null>> =>
  typeof value === "object" && value !== null && "value" in value;

export default defineComponent({
  components: {
    PlannerEvent,
    PlannerEventLight,
  },
  props: {
    calendarAPI: {
      type: Function as PropType<() => Calendar | undefined>,
      required: true,
    },
    selectedCalendarId: { type: String },
    end: { type: Date, required: false },
    extendedProps: { type: Object as PropType<Dictionary>, required: true },
    id: { type: String, required: true },
    isAllDay: { type: Boolean, required: true },
    isLeftPaneShown: { type: Boolean, required: true },
    isMoving: { type: Boolean, required: true },
    isRightClickAllowed: { type: Boolean, required: true },
    isSelected: { type: Boolean, required: true },
    isWeekView: { type: Boolean, required: true },
    // Display-only event: see Planner.vue's `readOnly` prop.
    readOnly: { type: Boolean, default: false },
    start: { type: Date, required: true },
    title: { type: String, required: true },
    // The hosting calendar's current zoom level, for PlannerEvent's layout
    // (see PlannerCalendar, which owns the persisted value).
    zoomLevel: { type: Number, required: true },
  },
  emits: {
    click: (_v: MouseEvent) => true,
    open: (
      _v: { kind: "task" | "event"; id: TaskId | EventId },
      _mouseEvent?: MouseEvent
    ) => true,
    removeEvent: (_: string) => true,
  },
  async setup(props, { emit }) {
    const {
      calendarAPI,
      selectedCalendarId,
      end: givenEnd,
      extendedProps,
      id,
      isAllDay,
      start: givenStart,
      title,
    } = toRefs(props);
    const controller = inject.controller();
    const calendars = inject.calendars();
    const logger = inject.logger("PlannerEventWrapper");
    const isAggregated = computed(() => isAggregatedAllDayEvent(id.value));
    // NB: task mirrors (dragging a task to create an allocation) can carry
    // either id shape of a generalized task — `task:…` or `object:…` for
    // database rows. Neither is an event id, so both must be treated as fake
    // events; falling through to `controller.event.get` would be a fatal
    // schema error.
    const isFakeEvent = computed(
      () =>
        isAggregated.value ||
        isStaticGhostEvent(id.value) ||
        isGeneralizedTaskId(id.value) ||
        isEmpty(id.value)
    );
    const isStatic = computed(() => isStaticGhostEvent(id.value));
    const calendarId = computed<string>(() => {
      // For aggregated all-day placeholders, keep the calendar tied to the aggregated events
      if (isAggregated.value) {
        return extendedProps.value.calendarId;
      }
      // For static ghost events (original allocation during drag), keep original calendar
      if (isStatic.value) {
        return extendedProps.value.calendarId;
      }
      // For task mirrors (dragging task to create allocation), follow the currently selected calendar
      if (isGeneralizedTaskId(id.value)) {
        return selectedCalendarId.value;
      }
      // For the select mirror (dragging an area to create an event, empty
      // id), preview the events default calendar, where the draft event will
      // be created — not the selected (task) calendar.
      return calendars.value?.default?.id;
    });
    const buildEnd = (start: Date, end: Date | undefined) => {
      if (
        !end ||
        // Event duration must be at least 30 minutes
        (isFakeEvent.value && getDuration(start, end) < 30)
      ) {
        return getMinimumEventEnd(start);
      } else return end;
    };
    // The reason is only logged: diagnostic trail for events vanishing right
    // after creation (removal funnels into killEventDetails in the parent).
    const removeEvent = (reason: string, args?: Record<string, unknown>) => {
      logger.info("Removing event from the calendar grid", {
        eventId: id.value,
        reason,
        ...args,
      });
      emit("removeEvent", id.value);
    };
    const releaser = useReleaser();
    const eventPromise = releaser.add(
      bindAsync(id, async (id) => {
        if (isFakeEvent.value) {
          const end = buildEnd(givenStart.value, givenEnd.value);
          // Use taskId from extendedProps for static ghost events
          // (allocations), otherwise fall back to the id itself for task
          // mirrors.
          const extendedTaskId = extendedProps.value?.taskId;
          const taskId =
            (typeof extendedTaskId === "string" ? extendedTaskId : undefined) ||
            (isGeneralizedTaskId(id) ? id : undefined);
          const event: ReleasableRef<FullEvent | null> = releasableRef(
            buildFakeEvent(
              id,
              title.value,
              isAllDay.value,
              givenStart.value,
              end,
              taskId,
              calendarId.value
            )
          );
          return event;
        }
        // Events built by the planner grid carry the live subscription that
        // `useEvents` owns: render from it instead of re-fetching. The
        // facade's no-op release keeps bindAsync/unmount from releasing a
        // ref this component does not own.
        const sharedRef: unknown = extendedProps.value?.eventRef;
        if (isSharedEventRef(sharedRef))
          return withRelease(
            computed(() => sharedRef.value),
            () => {}
          );
        return await controller.event.get(id);
      })
    );
    const taskPromise = releaser.add(
      eventPromise.then((event) =>
        bindAsync(event, async (event) =>
          event?.task
            ? await controller.task.get(event.task)
            : releasableRef(null)
        )
      )
    );
    const [event, task] = await Promise.all([eventPromise, taskPromise]);
    // Fake events (task mirrors) are built from `selectedCalendarId`,
    // which can be unset: render without a calendar then.
    const calendar = computed(() =>
      event.value?.calendar
        ? (calendars.value?.byId.get(event.value.calendar) ?? null)
        : null
    );
    // A missing event (deleted, possibly while rendered) removes it from
    // the calendar — this rode on the calendar re-bind before.
    releaser.defer(
      watch(
        event,
        (e) => {
          if (!e) removeEvent("event-not-found");
        },
        { immediate: true }
      )
    );

    // Update task mirror's calendar when selectedCalendarId changes (Tab
    // cycling during a drag). Do NOT change aggregated all-day placeholders',
    // static ghost events', or the select mirror's calendar.
    releaser.defer(
      watch(selectedCalendarId, (newCalendarId) => {
        if (
          newCalendarId &&
          !isAggregated.value &&
          !isStatic.value &&
          isGeneralizedTaskId(id.value) &&
          event.value
        ) {
          event.value.calendar = newCalendarId;
        }
      })
    );
    releaser.defer(
      watch(
        () => !!calendar.value?.disabled,
        (isDisabled) =>
          isDisabled
            ? removeEvent("calendar-disabled", {
                calendar: event.value?.calendar,
              })
            : null,
        { immediate: true }
      )
    );
    const forceUpdateEventInCalendar = (
      event: Ref<FullEvent | null | undefined>
    ) => {
      pause(() => {
        if (event?.value && !isFakeEvent.value) {
          const api = calendarAPI.value();
          if (api) {
            const calendarEvent = api.getEventById(event.value.id);
            if (calendarEvent) {
              // Don't update if the event already matches the given times (e.g., during drag/resize)
              const matchesGivenTimes =
                isEqual(event.value.start, givenStart.value) &&
                isEqual(event.value.end, givenEnd.value);

              // Don't update if FullCalendar already has the correct times
              const matchesCalendarTimes =
                isEqual(calendarEvent.start, event.value.start) &&
                isEqual(calendarEvent.end, event.value.end);

              if (!matchesGivenTimes && !matchesCalendarTimes) {
                calendarEvent.setDates(event.value.start, event.value.end, {
                  allDay: event.value.allDay,
                });
                patchSimultaneousEvents(calendarEvent, api.getEvents(), api);
              }
            }
          }
        }
      });
    };
    // Watch event.value directly to detect when backend updates the event times
    // (e.g., when completing an allocation moves the event to the past)
    releaser.defer(
      watch(
        () => event.value,
        (newEvent, oldEvent) => {
          // Only update if this is the same event and times actually changed
          if (newEvent && oldEvent && newEvent.id === oldEvent.id) {
            const timesChanged =
              !isEqual(newEvent.start, oldEvent.start) ||
              !isEqual(newEvent.end, oldEvent.end);

            if (timesChanged) {
              forceUpdateEventInCalendar(event);
            }
          }
        },
        { deep: true }
      )
    );

    // Allocation
    const overlappingIndex = computed<number>(
      () => extendedProps.value.overlappingIndex || 0
    );
    return {
      event,
      isFakeEvent,
      overlappingIndex,
      task,
    };
  },
});
</script>
<style scoped lang="scss">
.eventWrapper {
  width: 100%;
}
</style>
